// src/views/FullBookingPage/components/BookingDetails/index.js
import React, { useMemo, memo } from "react";
import { 
  formatDateToString, 
  formatDateForDisplay,
  createStableLocalDate,
  isSameDate,
  getPossibleDayValues,
  formatTimeDisplay,
  calculateDuration
} from "../../../utils/dateTimeHelpers";
import styles from "./styles.module.scss";

// SlotSummary component - shows count of slots with green checkmark
const SlotSummary = memo(({ slotCount, t }) => {
  if (slotCount === 0) return null;
  
  return (
    <span className={styles.slotSummaryBadge}>
      {t("booking.compactBookingForm.slotsSelected", { count: slotCount })}
      <span className={styles.checkmark}> ✓</span>
    </span>
  );
});
SlotSummary.displayName = 'SlotSummary';

// PersonSummary component - shows person count WITHOUT checkmark
const PersonSummary = memo(({ count, label, t }) => {
  if (count === 0) return null;
  
  const personLabel = count === 1 ? label : t("booking.compactBookingForm.people");
  
  return (
    <span className={styles.personSummaryBadge}>
      {count} {personLabel}
    </span>
  );
});
PersonSummary.displayName = 'PersonSummary';

// Minimalistic loading spinner
const MiniSpinner = memo(() => (
  <span className={styles.miniSpinner}></span>
));
MiniSpinner.displayName = 'MiniSpinner';

// Green checkmark SVG component
const GreenCheckmark = memo(() => (
  <svg 
    className={styles.checkmarkIcon} 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#22c55e" />
    <path 
      d="M8 12L11 15L16 9" 
      stroke="white" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
));
GreenCheckmark.displayName = 'GreenCheckmark';

const BookingDetails = ({ 
  isCoworking, 
  formData, 
  updateFormData, 
  maxCapacity,
  selectedDate,
  startTime,
  endTime,
  selectedDuration,
  availableSlots,
  durationOptions,
  availableDurations,
  currentMonth,
  setCurrentMonth,
  isCalendarOpen,
  setIsCalendarOpen,
  calendarRef,
  setSelectedDate,
  setSelectedDuration,
  setStartTime,
  handleAddTimeSlot,
  handleRemoveTimeSlot,
  slotError,
  getDaysInMonth,
  getDateStatus,
  formatDateToString: formatDateToStringProp,
  loadingAvailability,
  t,
  i18n,
  spaceOpeningHours,
  isUpdating = false,
  viewOnly = false,
  onEditClick,
  isSaving = false
}) => {
  const daysInMonth = getDaysInMonth(currentMonth);
  
  const availableDurationOptions = useMemo(() => {
    if (!selectedDate || loadingAvailability) {
      return durationOptions;
    }
    return durationOptions.filter(opt => availableDurations.includes(opt.value));
  }, [durationOptions, selectedDate, availableDurations, loadingAvailability]);
  
  const currentLanguage = i18n?.language || t("locale", "en");

  // Get slots from formData - filter out invalid slots
  const slots = useMemo(() => {
    const allSlots = formData.booking_hours || [];
    return allSlots.filter(slot => slot.date && slot.from && slot.to);
  }, [formData.booking_hours]);
  
  const slotCount = slots.length;
  const isOnlyOneSlot = slotCount === 1;

  // Get person count
  const personCount = isCoworking ? formData.coworking_stations || 0 : formData.capacity || 1;
  const personLabel = isCoworking 
    ? t("booking.compactBookingForm.station") 
    : t("booking.compactBookingForm.person");

  // Get display for the date button
  const getDisplayText = () => {
    if (viewOnly) return null;
    
    if (isCalendarOpen && selectedDate && startTime && endTime) {
      const display = formatDateForDisplay(selectedDate, currentLanguage);
      return `${display.dayOfMonth} ${display.monthYear} • ${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
    }
    
    if (isCalendarOpen && selectedDate) {
      const display = formatDateForDisplay(selectedDate, currentLanguage);
      return `${display.dayOfMonth} ${display.monthYear}`;
    }
    
    if (!isCalendarOpen && slotCount > 0) {
      return null; // Show SlotSummary with checkmark instead
    }
    
    return null;
  };

  const handleConfirmSlot = () => {
    if (selectedDate && startTime && endTime && !isUpdating && !viewOnly) {
      handleAddTimeSlot();
      setIsCalendarOpen(false);
    }
  };

  const currentMonthName = currentMonth.toLocaleString(currentLanguage, { month: "long" });
  const currentYear = currentMonth.getFullYear();

  // Format slot for display
  const formatSlotDisplay = (slot) => {
    const formattedFrom = formatTimeDisplay(slot.from);
    const formattedTo = formatTimeDisplay(slot.to);
    const dateDisplay = formatDateForDisplay(slot.date, currentLanguage);
    const duration = calculateDuration(formattedFrom, formattedTo);
    
    return {
      dateDisplay: `${dateDisplay.dayOfMonth} ${dateDisplay.monthYear}`,
      from: formattedFrom || '--:--',
      to: formattedTo || '--:--',
      duration: isNaN(duration) ? 0 : duration
    };
  };

  return (
    <div className={styles.bookingDetailsSection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.title}>
          {t("booking.fullBookingPage.bookingDetails.title")}
        </h2>
      </div>
      
      {/* Capacity - Grey badge WITHOUT checkmark */}
      <div className={styles.field}>
        <label>
          {isCoworking 
            ? t("booking.fullBookingPage.bookingDetails.workstations")
            : t("booking.fullBookingPage.bookingDetails.people")}
        </label>
        
        {viewOnly ? (
          // View-only: Show person count with grey badge (no checkmark)
          <div className={styles.personBadge}>
            <PersonSummary count={personCount} label={personLabel} t={t} />
          </div>
        ) : (
          // Editable: Show with +/- buttons
          <div className={styles.capacitySelector}>
            <button 
              onClick={() => updateFormData(isCoworking ? { coworking_stations: Math.max(1, (formData.coworking_stations || 0) - 1) } : { capacity: Math.max(1, (formData.capacity || 1) - 1) })}
              disabled={isCoworking ? (formData.coworking_stations || 0) <= 1 : (formData.capacity || 1) <= 1 || isUpdating}
            >
              -
            </button>
            <span>
              {isCoworking 
                ? `${formData.coworking_stations || 0} ${formData.coworking_stations === 1 ? t("booking.compactBookingForm.station") : t("booking.compactBookingForm.stations")}`
                : `${formData.capacity || 1} ${formData.capacity === 1 ? t("booking.compactBookingForm.person") : t("booking.compactBookingForm.people")}`}
            </span>
            <button 
              onClick={() => updateFormData(isCoworking ? { coworking_stations: Math.min(maxCapacity, (formData.coworking_stations || 0) + 1) } : { capacity: Math.min(maxCapacity, (formData.capacity || 1) + 1) })}
              disabled={isCoworking ? (formData.coworking_stations || 0) >= maxCapacity : (formData.capacity || 1) >= maxCapacity || isUpdating}
            >
              +
            </button>
          </div>
        )}
      </div>
      
      {/* Date picker - Only shown in editable mode */}
      {!viewOnly && (
        <div className={styles.field}>
          <label>{t("booking.fullBookingPage.bookingDetails.date")}</label>
          <div className={styles.datePickerWrapper}>
            <button 
              className={`${styles.datePickerButton} ${isUpdating ? styles.operationInProgress : ''}`}
              onClick={() => setIsCalendarOpen(!isCalendarOpen)} 
              type="button"
              disabled={isUpdating}
            >
              {slotCount > 0 && !isCalendarOpen ? (
                <span className={styles.selectedDate}>
                  <span className={styles.dateDisplay}>
                    <SlotSummary slotCount={slotCount} t={t} />
                    {isUpdating && <MiniSpinner />}
                  </span>
                </span>
              ) : getDisplayText() ? (
                <span className={styles.selectedDate}>
                  <span className={styles.dateDisplay}>
                    {getDisplayText()}
                  </span>
                </span>
              ) : (
                <span className={styles.placeholderDate}>
                  {t("booking.fullBookingPage.bookingDetails.selectDate")}
                </span>
              )}
              <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </button>
            
            {isCalendarOpen && (
              <div className={`${styles.calendarPopup} ${selectedDate ? styles.hasDateSelected : ''}`} ref={calendarRef}>
                <div className={styles.calendarSection}>
                  <div className={styles.calendarHeader}>
                    <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>‹</button>
                    <span>
                      {currentMonthName} {currentYear}
                    </span>
                    <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>›</button>
                  </div>
                  <div className={styles.weekdays}>
                    {[
                      t("days.short.monday", "Mon"),
                      t("days.short.tuesday", "Tue"),
                      t("days.short.wednesday", "Wed"),
                      t("days.short.thursday", "Thu"),
                      t("days.short.friday", "Fri"),
                      t("days.short.saturday", "Sat"),
                      t("days.short.sunday", "Sun")
                    ].map(day => (
                      <span key={day}>{day}</span>
                    ))}
                  </div>
                  <div className={styles.days}>
                    {loadingAvailability && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.smallSpinner}></div>
                      </div>
                    )}
                    {daysInMonth.map((date, idx) => {
                      if (!date) return <span key={`empty-${idx}`} className={styles.emptyDay} />;
                      
                      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                      const status = getDateStatus(date);
                      const isSelected = selectedDate && isSameDate(date, selectedDate);
                      const isDisabled = status === 'disabled' || status === 'past' || status === 'unavailable';
                      
                      return (
                        <button 
                          key={dateKey} 
                          className={`${styles.day} ${styles[status]} ${isSelected ? styles.selected : ""}`}
                          onClick={() => {
                            if (!isDisabled && !isUpdating) {
                              setSelectedDate(date);
                              setSelectedDuration(null);
                              setStartTime(null);
                            }
                          }} 
                          disabled={isDisabled || isUpdating} 
                          type="button"
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {selectedDate && (
                  <div className={styles.calendarSelectionSection}>
                    <div className={styles.durationField}>
                      <label>{t("booking.fullBookingPage.bookingDetails.duration")}</label>
                      {loadingAvailability ? (
                        <p className={styles.loadingMessage}>{t("booking.fullBookingPage.bookingDetails.loadingDurations")}</p>
                      ) : availableDurationOptions.length > 0 ? (
                        <div className={styles.durationOptions}>
                          {availableDurationOptions.map(opt => (
                            <button 
                              key={opt.value} 
                              className={`${styles.durationChip} ${selectedDuration === opt.value ? styles.selected : ""}`}
                              onClick={() => setSelectedDuration(opt.value)} 
                              type="button"
                              disabled={isUpdating}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className={styles.noDurationsMessage}>{t("booking.fullBookingPage.bookingDetails.noDurations")}</p>
                      )}
                    </div>
                    
                    {selectedDuration && (
                      <div className={styles.timeField}>
                        <label>{t("booking.fullBookingPage.bookingDetails.startTime")}</label>
                        {loadingAvailability ? (
                          <p className={styles.loadingMessage}>{t("booking.fullBookingPage.bookingDetails.loadingTimeSlots")}</p>
                        ) : availableSlots.length > 0 ? (
                          <div className={styles.timeOptions}>
                            {availableSlots.map(slot => (
                              <button 
                                key={slot} 
                                className={`${styles.timeChip} ${startTime === slot ? styles.selected : ""}`}
                                onClick={() => setStartTime(slot)} 
                                type="button"
                                disabled={isUpdating}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className={styles.noSlotsMessage}>{t("booking.fullBookingPage.bookingDetails.noSlots")}</p>
                        )}
                      </div>
                    )}
                    
                    {selectedDuration && startTime && endTime && (
                      <button 
                        className={`${styles.confirmSlotButton} ${isUpdating ? styles.loading : ''}`}
                        onClick={handleConfirmSlot}
                        type="button"
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <span className={styles.buttonLoadingSpinner}></span>
                        ) : (
                          t("booking.fullBookingPage.bookingDetails.confirmSlot")
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* View-only: Show date label and slots WITHOUT remove button */}
      {viewOnly && (
        <div className={styles.field}>
          <label>{t("booking.fullBookingPage.bookingDetails.date")}</label>
          <div className={styles.viewOnlyDateDisplay}>
            {slots.map((slot, index) => {
              const formatted = formatSlotDisplay(slot);
              return (
                <div key={index} className={styles.viewOnlySlotItem}>
                  <GreenCheckmark />
                  <span>
                    {formatted.dateDisplay}
                    {' • '}{formatted.from} - {formatted.to}
                    <span className={styles.slotDuration}>
                      ({formatted.duration}h)
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Edit Link */}
          <div className={styles.editLinkWrapper}>
            <button 
              className={styles.editLink} 
              onClick={onEditClick}
              disabled={isSaving}
              type="button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {t("booking.fullBookingPage.editBooking.edit", "Edit")}
            </button>
          </div>
        </div>
      )}
      
      {/* Selected Slots List - With Green Checkmark on the LEFT side (editable mode) */}
      {!viewOnly && slotCount > 0 && (
        <div className={styles.selectedSlotsList}>
          {slots.map((slot, index) => {
            const formatted = formatSlotDisplay(slot);
            
            return (
              <div key={index} className={styles.selectedSlotItem}>
                <div className={styles.slotInfoWrapper}>
                  <GreenCheckmark />
                  <span className={styles.slotInfo}>
                    {formatted.dateDisplay}
                    {' • '}{formatted.from} - {formatted.to}
                    <span className={styles.slotDuration}>
                      ({formatted.duration}h)
                    </span>
                  </span>
                </div>
                
                {/* Remove button */}
                <button
                  className={`${styles.removeSlotBtn} ${isOnlyOneSlot ? styles.disabled : ''}`}
                  onClick={() => {
                    if (handleRemoveTimeSlot && !isOnlyOneSlot && !isUpdating) {
                      handleRemoveTimeSlot(index);
                    }
                  }}
                  disabled={isOnlyOneSlot || isUpdating}
                  type="button"
                  title={isOnlyOneSlot ? t("booking.fullBookingPage.errors.cannotRemoveLastSlot") : ""}
                >
                  {isUpdating ? (
                    <span className={styles.miniSpinner}></span>
                  ) : (
                    "✕"
                  )}
                </button>
              </div>
            );
          })}
          <div className={styles.slotsCount}>
            {isUpdating ? (
              <span className={styles.savingText}>Saving...</span>
            ) : (
              `${slotCount} ${slotCount === 1 ? t("booking.compactBookingForm.slot") : t("booking.compactBookingForm.slots")}`
            )}
          </div>
        </div>
      )}
      
      {slotError && <div className={styles.error}>{slotError}</div>}
    </div>
  );
};

export default BookingDetails;